import { postToHost } from '@/shared/bridge/bridge'
import { hasHostShell } from '@/shared/bridge/env'

export interface OmsOpenOptions {
  path?: string
  search?: string
  title?: string
  menuName?: string
}

const LOCAL_OMS_WEB_PORT = 40002
const LOCAL_WEB_HOST_KEY = 'qsc.localWebHost'
const DEFAULT_OMS_WEB_URL = 'https://dev-oms.bizbee.ne.kr'

const OMS_WEB_URL_BY_QSC_ORIGIN: Record<string, string> = {
  'https://dev-qscv.bizbee.ne.kr': 'https://dev-oms.bizbee.ne.kr',
  'https://qscv-app.bizbee.co.kr': 'https://oms-app.bizbee.co.kr',
}

function withSearch(url: URL, search?: string) {
  if (search) {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    params.forEach((value, key) => url.searchParams.set(key, value))
  }
  return url.toString()
}

function isAbsoluteUrl(value: string) {
  try {
    new URL(value)
    return true
  } catch {
    return false
  }
}

function markQscShell(url: URL) {
  url.searchParams.set('embedHost', 'qsc')
  url.searchParams.set('shell', 'external')
  const localWebHost = getLocalWebHost()
  if (localWebHost) url.searchParams.set('localWebHost', localWebHost)
  return url
}

function sanitizeLocalWebHost(value: string | null) {
  const trimmed = value?.trim()
  if (!trimmed) return null
  return trimmed.replace(/^https?:\/\//, '').split('/')[0].split(':')[0] || null
}

function getLocalWebHost() {
  if (typeof window === 'undefined') return null

  const params = new URLSearchParams(window.location.search)
  const hostFromUrl = sanitizeLocalWebHost(params.get('localWebHost'))
  if (hostFromUrl) {
    try {
      window.sessionStorage.setItem(LOCAL_WEB_HOST_KEY, hostFromUrl)
    } catch {
      // Session storage can be unavailable inside embedded browsers.
    }
    return hostFromUrl
  }

  try {
    return sanitizeLocalWebHost(window.sessionStorage.getItem(LOCAL_WEB_HOST_KEY))
  } catch {
    return null
  }
}

function getOmsBaseUrl() {
  if (typeof window !== 'undefined') {
    if (import.meta.env.DEV) {
      const host = getLocalWebHost() ?? window.location.hostname
      return `${window.location.protocol}//${host}:${LOCAL_OMS_WEB_PORT}`
    }

    const mapped = OMS_WEB_URL_BY_QSC_ORIGIN[window.location.origin]
    if (mapped) return mapped
  }

  return DEFAULT_OMS_WEB_URL
}

export function getOmsUrl(options: OmsOpenOptions = {}): string {
  if (options.path && isAbsoluteUrl(options.path)) {
    return withSearch(markQscShell(new URL(options.path)), options.search)
  }

  const url = new URL(getOmsBaseUrl(), window.location.origin)
  if (options.path) url.pathname = options.path.startsWith('/') ? options.path : `/${options.path}`
  markQscShell(url)
  return withSearch(url, options.search)
}

export function requestOmsOpen(options: OmsOpenOptions = {}): 'host-switch' | 'iframe-open' {
  if (hasHostShell()) {
    postToHost({ type: 'SWITCH_TO_OMS', payload: { path: getOmsUrl(options) } })
    return 'host-switch'
  }

  window.dispatchEvent(new CustomEvent<OmsOpenOptions>('qsc:open-oms', { detail: options }))
  return 'iframe-open'
}
